import React, { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { http } from "../lib/api";
import { PdfItem } from "./PdfItem";
import { PdfPreviewBlock } from "./PdfPreviewBlock";

/**
 * Compact "Most Viewed This Week" rail.
 * - Renders only when the API returns at least one popular PDF
 * - Hidden when a category filter or search query is active (caller decides)
 * - Each card uses the existing PdfItem in compact/grid mode (no inline preview).
 *   When a card is expanded, the full-width preview is rendered directly below
 *   the rail — matching the Featured section's behaviour.
 */
export function PopularRail({ expandedId, onToggleExpand }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/pdfs/popular", { params: { days: 7, limit: 6 } });
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      } catch {
        // Non-fatal — rail simply stays hidden if the endpoint fails.
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || items.length === 0) return null;

  const visible = items.slice(0, 6);
  const expandedItem = visible.find((p) => p.id === expandedId);

  return (
    <section className="mb-10" data-testid="popular-rail">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
            <Flame className="h-6 w-6 text-brand-accent fill-brand-accent" />
            Most Viewed This Week
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            What your visitors are actually opening right now.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="popular-grid">
        {visible.map((pdf) => (
          <PdfItem
            key={`pop-${pdf.id}`}
            pdf={pdf}
            view="grid"
            compact
            renderInlinePreview={false}
            expanded={expandedId === pdf.id}
            onToggleExpand={() => onToggleExpand(pdf.id)}
          />
        ))}
      </div>

      {expandedItem && (
        <div className="mt-5">
          <PdfPreviewBlock
            pdf={expandedItem}
            fullWidth
            onClose={() => onToggleExpand(expandedItem.id)}
          />
        </div>
      )}
    </section>
  );
}
